import i18next from "i18next";

import { API_ENDPOINTS, SPORT_TEST_MODE } from "@/configs";
import apiRequest from "@/lib/axios";
import sportApiRequest from "@/lib/sportAxios";
import { OddsChangePolicy } from "@/store/reducers/sportPrefs";
import {
  BetSelection,
  BetType,
  EventDetailResponse,
  EventsByLeagueResponse,
  EventsByStatusResponse,
  PlaceBetResult,
  SportBetHistoryParams,
  SportBetHistoryResponse,
  SportBetLimits,
  SportCashoutQuote,
  SportCashoutResult,
  SportEvent,
  SportFavorite,
  SportFavoriteKind,
  SportGrant,
  SportListResponse,
  SportLiveInfo,
  SportLobbyResponse,
  SportPromotions,
  SportRegionGroup,
  SportSearchEvent,
  SportsTreeResponse,
} from "@/types";

export const getSports = async (status = "all"): Promise<SportListResponse> => {
  const res = await sportApiRequest<SportListResponse>({
    method: "GET",
    url: API_ENDPOINTS.SPORT.LIST,
    params: { status },
    errorMessage: i18next.t("sport.load-error"),
  });
  return res;
};

export const getEventsBySport = (
  sportSlug: string,
  eventStatus: string,
): Promise<SportRegionGroup> => {
  return sportApiRequest<SportRegionGroup>({
    method: "GET",
    url: API_ENDPOINTS.SPORT.EVENTS_BY_SPORT,
    params: { sportSlug, eventStatus },
    errorMessage: i18next.t("sport.matches-error"),
  });
};

export const getEventsByStatus = (eventStatus: string): Promise<EventsByStatusResponse> => {
  return sportApiRequest<EventsByStatusResponse>({
    method: "GET",
    url: API_ENDPOINTS.SPORT.EVENTS_BY_STATUS,
    params: { eventStatus },
    errorMessage: i18next.t("sport.matches-error"),
  });
};

export const getEvent = async (eventId: string): Promise<SportEvent> => {
  const res = await sportApiRequest<EventDetailResponse>({
    method: "GET",
    url: API_ENDPOINTS.SPORT.EVENT_DETAIL(eventId),
    params: SPORT_TEST_MODE ? { allMarkets: "1" } : undefined,
    errorMessage: i18next.t("sport.event-error"),
  });

  return res.event;
};

export interface PlaceBetOptions {
  systemType?: string;
  acceptOddsChange?: OddsChangePolicy;
  /** A free bet or profit boost the slip uses. */
  grant?: SportGrant | null;
}

export const placeBet = async (
  betType: BetType,
  bets: BetSelection[],
  options: PlaceBetOptions = {},
): Promise<PlaceBetResult> => {
  const boostId = betType === "single" && bets.length === 1 ? bets[0]?.boostId : undefined;
  return apiRequest<PlaceBetResult>({
    method: "POST",
    url: API_ENDPOINTS.SPORT.PLACE_BET,
    data: {
      mode: betType,
      multiStake: betType === "single" ? 0 : (bets[0]?.stake ?? 0),
      systemType: betType === "system" ? options.systemType : undefined,
      // A boost pays its own price; the market price it is placed at may have moved.
      acceptOddsChange: boostId ? "any" : options.acceptOddsChange,
      freeBetId: options.grant?.kind === "free_bet" ? options.grant.id : undefined,
      profitBoostId: options.grant?.kind === "profit_boost" ? options.grant.id : undefined,
      oddsBoostId: boostId,
      selections: bets.map((bet) => {
        const oddValue = Number(bet.odds).toFixed(3);
        const hdpPart = bet.hdp != null ? String(bet.hdp) : "";
        // The line travels as a number: Betaxis settles spreads and totals with it.
        const handicap =
          bet.hdp != null && Number.isFinite(Number(bet.hdp)) ? Number(bet.hdp) : undefined;

        return {
          id: `${bet.eventId}__${bet.marketName}__${bet.selectionLabel}__${bet.selectionSide}__${hdpPart}__${oddValue}`,
          eventId: bet.eventId,
          eventName: `${bet.home} vs ${bet.away}`,
          sportName: bet.sportName,
          leagueName: bet.leagueName,
          marketName: bet.marketName,
          oddName: bet.selectionLabel,
          oddValue,
          handicap,
          stake: bet.stake,
          selectionSide: bet.selectionSide,
        };
      }),
    },
    // Empty: the slip falls back to its own translated message when the server gives none.
    errorMessage: "",
  });
};

export const getSportBets = async ({
  page,
  pageSize,
  status,
  mode,
  from,
  to,
}: SportBetHistoryParams): Promise<SportBetHistoryResponse> => {
  return apiRequest<SportBetHistoryResponse>({
    method: "GET",
    url: API_ENDPOINTS.SPORT.BET_HISTORY,
    // Statuses go as one comma list; an unset filter is left off the query.
    params: {
      page,
      pageSize,
      status: status?.length ? status.join(",") : undefined,
      mode: mode || undefined,
      from: from || undefined,
      to: to || undefined,
    },
    errorMessage: i18next.t("sports-bet.load-error"),
  });
};

export const searchSportEvents = async (q: string): Promise<SportSearchEvent[]> => {
  const res = await sportApiRequest<{ events: SportSearchEvent[] }>({
    method: "GET",
    url: API_ENDPOINTS.SPORT.SEARCH,
    params: { q },
    errorMessage: i18next.t("sport.search-error"),
  });
  return res.events ?? [];
};

export const getEventLive = async (eventId: string): Promise<SportLiveInfo> => {
  const res = await sportApiRequest<{ live: SportLiveInfo }>({
    method: "GET",
    url: API_ENDPOINTS.SPORT.EVENT_LIVE(eventId),
    errorMessage: i18next.t("sport.tracker-error"),
  });
  return res.live;
};

export const getSportPromotions = (): Promise<SportPromotions> =>
  apiRequest<SportPromotions>({
    method: "GET",
    url: API_ENDPOINTS.SPORT.PROMOTIONS,
    errorMessage: "",
  });

export const getSportCashoutQuotes = async (betIds: string[]): Promise<SportCashoutQuote[]> => {
  const res = await apiRequest<{ quotes: SportCashoutQuote[] }>({
    method: "POST",
    url: API_ENDPOINTS.SPORT.CASHOUT_QUOTES,
    data: { betIds },
    errorMessage: "",
  });
  return res.quotes ?? [];
};

export const cashOutSportBet = (
  betId: string,
  quotedValue: number,
  amount?: number,
): Promise<SportCashoutResult> =>
  apiRequest<SportCashoutResult>({
    method: "POST",
    url: API_ENDPOINTS.SPORT.CASHOUT(betId),
    data: { quotedValue, ...(amount !== undefined ? { amount } : {}) },
    errorMessage: i18next.t("sports-bet.cashout-failed"),
  });

export const setSportAutoCashout = (betId: string, targetValue: number | null) =>
  apiRequest<{ autoCashout: { targetValue: number } | null }>({
    method: "PUT",
    url: API_ENDPOINTS.SPORT.AUTO_CASHOUT(betId),
    data: { targetValue },
    errorMessage: i18next.t("sports-bet.cashout-failed"),
  });

export const getSportFavorites = async (): Promise<SportFavorite[]> => {
  const res = await apiRequest<{ favorites: SportFavorite[] }>({
    method: "GET",
    url: API_ENDPOINTS.SPORT.FAVORITES,
    errorMessage: "",
  });
  return res.favorites ?? [];
};

export const toggleSportFavorite = async (
  favorite: { kind: SportFavoriteKind; key: string; label: string; sportSlug?: string },
  follow: boolean,
): Promise<SportFavorite[]> => {
  const res = await apiRequest<{ favorites: SportFavorite[] }>({
    method: "POST",
    url: follow ? API_ENDPOINTS.SPORT.FAVORITES : API_ENDPOINTS.SPORT.FAVORITES_REMOVE,
    data: favorite,
    errorMessage: i18next.t("sport.favorite-error"),
  });
  return res.favorites ?? [];
};

/** Sports → regions → leagues with open-event counts, for the sidebar tree. */
export const getSportsTree = (): Promise<SportsTreeResponse> =>
  sportApiRequest<SportsTreeResponse>({
    method: "GET",
    url: API_ENDPOINTS.SPORT.SPORTS_TREE,
    errorMessage: i18next.t("sport.load-error"),
  });

/** The lobby: top live matches, next kick-offs, featured leagues and sport counts. */
export const getSportLobby = (): Promise<SportLobbyResponse> =>
  sportApiRequest<SportLobbyResponse>({
    method: "GET",
    url: API_ENDPOINTS.SPORT.LOBBY,
    errorMessage: i18next.t("sport.matches-error"),
  });

export const getEventsByRegion = (
  sportSlug: string,
  regionSlug: string,
  eventStatus: string,
): Promise<SportRegionGroup> =>
  sportApiRequest<SportRegionGroup>({
    method: "GET",
    url: API_ENDPOINTS.SPORT.EVENTS_BY_REGION,
    params: { sportSlug, regionSlug, eventStatus },
    errorMessage: i18next.t("sport.matches-error"),
  });

export const getEventsByLeague = (
  sportSlug: string,
  regionSlug: string,
  leagueSlug: string,
  eventStatus: string,
): Promise<EventsByLeagueResponse> =>
  sportApiRequest<EventsByLeagueResponse>({
    method: "GET",
    url: API_ENDPOINTS.SPORT.EVENTS_BY_LEAGUE,
    params: { sportSlug, regionSlug, leagueSlug, eventStatus },
    errorMessage: i18next.t("sport.matches-error"),
  });

/** The sportsbook's intake limits; public, so the slip can show them before sign-in. */
export const getSportBetLimits = (): Promise<SportBetLimits> =>
  apiRequest<SportBetLimits>({
    method: "GET",
    url: API_ENDPOINTS.SPORT.BET_LIMITS,
    errorMessage: "",
  });
